import React from 'react'
import withStyles from '@material-ui/core/styles/withStyles'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import Icon from '@material-ui/core/Icon'
import DropDown from '../SegmentationQuery/DropDown'
import { withApi } from '~shared/api/ApiContext'
import ConditionsBlocks from '~components/atom/FieldFactory/ConditionsBlocks'
import CustomReference from '~components/atom/FieldFactory/CustomReference'
import {
  CustomDate,
  CustomRangeDate,
} from '~shared/components/atoms/CustomDatePicker/CustomPickers'
import CustomNumber from '~components/atom/FieldFactory/CustomNumber'
import CustomMenuField from '~components/atom/FieldFactory/CustomMenuField'

/**
 * @typedef {Object} BadgeSetting
 * @property {string} id - The unique identifier for the badge.
 * @property {('times'|'view'|'points'|'date'|'sign')} type - The type of the badge.
 * @property {string} label - The label for the badge.
 * @property {string} [model] - The model associated with the badge.
 * @property {boolean} [withPeriod] - Indicates if the badge has a period.
 * @property {boolean} [withRepeat] - Indicates if the badge has a period.
 * @property {Array.<BadgeOption>} [options] - The options for the badge.
 */

/**
 * @typedef {Object} BadgeOption
 * @property {string} value - The value of the option.
 * @property {string} label - The label of the option.
 */

const SUM_SIZE_BLOCKS = 12

const styles = () => ({
  condition: {
    width: '100%',
    margin: 0,
    borderRadius: 5,
    border: '1px solid rgba(0,0,0,0.1)',
  },
  w100child: {
    '& > *': {
      width: '100%',
    },
  },
})

class BadgeConditions extends React.Component {
  constructor(props) {
    super(props)

    this.state = {}
  }

  async componentDidMount() {
    const { api } = this.props

    let { request } = api.getBadgeConditionOptions()

    let options
    let repeatOptions
    try {
      ;({ options, repeatOptions } = (await request) || {})
      options = options?.map(o => ({ ...o, value: o.id }))
    } catch (err) {
      return
    }

    this.setState({ options, repeatOptions })
  }

  _getConditionByIndex = index => {
    const { value: query } = this.props
    return query[index]
  }

  _updateCondition = (index, data) => {
    const { value: query } = this.props

    let condition = this._getConditionByIndex(index)

    const newQuery = [...query]
    condition = { ...condition, ...data }
    newQuery[index] = condition

    const { onChange } = this.props

    onChange({
      target: {
        value: newQuery,
        name: this.props.name,
      },
    })
  }

  handleFieldChange = index => e => {
    const { value } = e.target

    let data = {
      id: value,
      reference: null,
    }

    this._updateCondition(index, data)
  }

  handleRemoveCondition = index => () => {
    const { value: query, onChange } = this.props

    const newQuery = [...query]
    newQuery.splice(index, 1)

    onChange({
      target: {
        value: newQuery,
        name: this.props.name,
      },
    })
  }

  /**
   * @param {BadgeSetting} settings
   * @param item
   * @param i
   * @return {JSX.Element}
   */
  renderReference = (settings, item, i) => {
    const { model } = settings
    const FIELD = 'reference'

    const onChange = ({ target: { value } }) => {
      this._updateCondition(i, { [FIELD]: value })
    }

    const isVirtual = model === '~sitemap'

    return (
      <CustomReference
        type={isVirtual ? 'vreference' : 'reference'}
        settings={{ collection: model, disableCreatingItem: true }}
        onChange={onChange}
        value={item[FIELD]}
        label="Reference"
        isInlined
        disabled={this.props.disabled}
      />
    )
  }

  /**
   * @param {BadgeSetting} settings
   * @param item
   * @param i
   * @return {JSX.Element}
   */
  renderPeriod = (settings, item, i) => {
    const FIELD = 'period'
    const onChange = ({ target: { value } }) => {
      this._updateCondition(i, { [FIELD]: value })
    }

    return (
      <CustomRangeDate
        onChange={onChange}
        value={item[FIELD]}
        label="Period"
        disabled={this.props.disabled}
      />
    )
  }

  /**
   * @param {BadgeSetting} settings
   * @param item
   * @param i
   * @return {JSX.Element}
   */
  renderTimes = (settings, item, i) => {
    const FIELD = 'times'
    const onUpdate = value => {
      this._updateCondition(i, { [FIELD]: value })
    }

    return (
      <CustomNumber
        onUpdate={onUpdate}
        value={item[FIELD] || 0}
        label="Times"
        disabled={this.props.disabled}
      />
    )
  }

  /**
   * @param {BadgeSetting} settings
   * @param item
   * @param i
   * @return {JSX.Element}
   */
  renderRepeatSettings = (settings, item, i) => {
    const onChange = ({ target: { value } }) => {
      console.log(value, item)
      this._updateCondition(i, { repeat: value })
    }
    const onUpdate = value => {
      this._updateCondition(i, { duration: value })
    }

    return [
      <CustomMenuField
        onChange={onChange}
        value={item.repeat || 'none'}
        options={this.state.repeatOptions}
        label="Repeat"
        disabled={this.props.disabled}
      />,
      <CustomNumber
        onUpdate={onUpdate}
        value={item.duration || 0}
        label="Duration"
        disabled={!item?.repeat || item?.repeat === 'none' || this.props.disabled}
      />,
    ]
  }

  /**
   * @param {BadgeSetting} settings
   * @param item
   * @param i
   * @return {JSX.Element}
   */
  renderSign = (settings, item, i) => {
    const FIELD = 'documentType'
    const onChange = ({ target: { value } }) => {
      this._updateCondition(i, { [FIELD]: value })
    }

    return (
      <CustomMenuField
        onChange={onChange}
        value={item[FIELD]}
        options={settings.options}
        label="Document Type"
        disabled={this.props.disabled}
      />
    )
  }

  /**
   * @param {BadgeSetting} settings
   * @param item
   * @param i
   * @return {JSX.Element}
   */
  renderDate = (settings, item, i) => {
    const FIELD = 'date'
    const onChange = ({ target: { value } }) => {
      this._updateCondition(i, { [FIELD]: value })
    }

    return (
      <CustomDate
        onChange={onChange}
        value={item[FIELD]}
        label="Date"
        disabled={this.props.disabled}
      />
    )
  }

  /**
   * @param {BadgeSetting} settings
   * @param item
   * @param i
   * @return {JSX.Element[]}
   */
  renderSettings = (settings, item, i) => {
    if (!settings) return null
    const { type, model } = settings

    let renderElement = []

    if (model) {
      renderElement.push(this.renderReference(settings, item, i))
    }

    switch (type) {
      case 'points':
      case 'view':
        break
      case 'times':
        renderElement.push(this.renderTimes(settings, item, i))
        // if (withRepeat) renderElement.push(this.renderRepeatSettings(settings, item, i))
        break
      case 'sign':
        if (settings.options) renderElement.push(this.renderSign(settings, item, i))
        break
      case 'date':
        // renderElement.push(this.renderDate(settings, item, i))
        break
      default:
        break
    }

    // if (withPeriod) renderElement.push(this.renderPeriod(settings, item, i))

    renderElement = renderElement.flat()

    const length = renderElement.length

    const sizeByBlock = Math.floor(SUM_SIZE_BLOCKS / length)
    const sizeLastBlock = sizeByBlock + (SUM_SIZE_BLOCKS % length)

    return renderElement.map((element, index) => (
      <Grid
        item
        xs={index === length - 1 ? sizeLastBlock : sizeByBlock}
        key={index}
        className={this.props.classes.w100child}
      >
        {element}
      </Grid>
    ))
  }

  renderValue = (item, i) => {
    const { classes, value = [{}], disabled: queryDisabled = false } = this.props
    const { id } = item
    const { options } = this.state
    let disabledRemoving = value.length === 1

    const optionsLoaded = !!options
    let disabled = queryDisabled || !optionsLoaded

    const settings = options && options.find(o => o.id === id)

    return (
      <Grid
        container
        spacing={16}
        alignItems={'center'}
        className={classes.condition}
        key={`condition_${i}_${id}`}
      >
        <Grid item xs={4}>
          <DropDown
            onChange={this.handleFieldChange(i)}
            disabled={disabled}
            value={id}
            options={options}
            label="Type"
          />
        </Grid>
        <Grid item container xs={8} wrap={'nowrap'}>
          <Grid container xs={12} spacing={16}>
            {this.renderSettings(settings, item, i)}
          </Grid>
          <IconButton
            onClick={this.handleRemoveCondition(i)}
            disabled={disabled || disabledRemoving}
          >
            <Icon>delete</Icon>
          </IconButton>
        </Grid>
      </Grid>
    )
  }

  render() {
    return (
      <ConditionsBlocks
        {...this.props}
        renderValue={this.renderValue}
        NestedComponent={BadgeConditions}
        blockFieldName="conditions"
        withoutTypeQuery
        withoutBlocks
      />
    )
  }
}

export default withApi(withStyles(styles)(BadgeConditions))
